import { useState, useEffect, createContext, useContext, useCallback } from 'react';
import { useAuth, useUser } from '@clerk/clerk-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Create context for tenant data
const TenantContext = createContext(null);

export function useTenant() {
  return useContext(TenantContext);
}

/**
 * TenantProvider fetches and provides tenant context for authenticated users.
 * Handles user sync and onboarding state detection.
 */
export function TenantProvider({ children }) {
  const { getToken, isLoaded, isSignedIn } = useAuth();
  const { user: clerkUser } = useUser();
  const [tenant, setTenant] = useState(null);
  const [loading, setLoading] = useState(true);
  const [needsOnboarding, setNeedsOnboarding] = useState(false);
  const [error, setError] = useState(null);

  const syncAndCheckAuth = useCallback(async () => {
    if (!isLoaded || !isSignedIn) {
      setLoading(false);
      return;
    }

    try {
      const token = await getToken();
      if (!token) {
        setLoading(false);
        return;
      }

      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      };

      // Step 1: Sync user with backend (creates if new)
      const syncResponse = await fetch(`${BACKEND_URL}/api/users/sync`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          email: clerkUser?.primaryEmailAddress?.emailAddress,
          first_name: clerkUser?.firstName,
          last_name: clerkUser?.lastName,
        }),
      });

      if (!syncResponse.ok) {
        throw new Error('User sync failed');
      }

      const syncData = await syncResponse.json();

      if (syncData.has_business) {
        // User has a business
        if (syncData.user) {
          // Optimized path: Use the user data returned from sync
          setTenant({
            authenticated: true,
            user_id: syncData.user_id,
            clerk_user_id: syncData.clerk_user_id,
            email: syncData.email,
            has_business: true,
            user: syncData.user,
          });
        } else {
          // Legacy path: fetch full tenant context
          const userResponse = await fetch(`${BACKEND_URL}/api/users/me`, { headers });
          
          if (userResponse.ok) {
            const userData = await userResponse.json();
            setTenant({
              authenticated: true,
              user_id: syncData.user_id,
              clerk_user_id: syncData.clerk_user_id,
              email: syncData.email,
              has_business: true,
              user: userData,
            });
          }
        }
        setNeedsOnboarding(false);
      } else {
        // User needs to create a business (new user or no business yet)
        setTenant({
          authenticated: true,
          user_id: syncData.user_id,
          clerk_user_id: syncData.clerk_user_id,
          email: syncData.email,
          has_business: false,
        });
        setNeedsOnboarding(true);
      }
    } catch (err) {
      console.error('Tenant sync error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [isLoaded, isSignedIn, getToken, clerkUser]);

  useEffect(() => {
    syncAndCheckAuth();
  }, [syncAndCheckAuth]);

  const refreshTenant = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const token = await getToken();
      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      };

      const userResponse = await fetch(`${BACKEND_URL}/api/users/me`, { headers });
      
      if (userResponse.ok) {
        const userData = await userResponse.json();
        setTenant(prev => ({
          ...prev,
          user: userData,
          has_business: true,
        }));
        setNeedsOnboarding(false);
      } else {
        // If user doesn't exist yet or API fails, don't change onboarding state
        console.error('Failed to fetch user data:', userResponse.status);
        throw new Error('Failed to refresh tenant data');
      }
    } catch (err) {
      console.error('RefreshTenant error:', err);
      setError(err.message);
      // Don't change needsOnboarding or has_business on error
    } finally {
      setLoading(false);
    }
  }, [getToken]);

  return (
    <TenantContext.Provider value={{ 
      tenant, 
      loading, 
      needsOnboarding, 
      error,
      refreshTenant,
      getToken 
    }}>
      {children}
    </TenantContext.Provider>
  );
}
